import type {
  ContentBundle,
  ControllerSnapshot,
  GameAction,
  GameControllerPort,
  GameState,
  GridPoint,
  P0Layout,
  Rect
} from "@h5-rpg/contracts";
import { reduceGame } from "@h5-rpg/core";

const contains = (rect: Rect, x: number, y: number): boolean =>
  x >= rect.x &&
  y >= rect.y &&
  x <= rect.x + rect.width &&
  y <= rect.y + rect.height;

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

export interface RpgMapLayout {
  width: number;
  height: number;
  mode: "portrait" | "landscape";
  hudHeight: number;
  navigationHeight: number;
  mapViewport: Rect & {
    cellSize: number;
    columns: number;
    rows: number;
    originX: number;
    originY: number;
  };
}

/**
 * Shared camera contract for grid RPGs.
 *
 * Desktop uses the full game width and a fixed-size camera instead of scaling
 * an entire map into a phone-like card. Large and small maps therefore keep
 * the same visual scale while the camera follows the player.
 */
export const computeRpgMapLayout = (
  width: number,
  height: number,
  mapWidth: number,
  mapHeight: number,
  player: GridPoint
): RpgMapLayout => {
  const portrait = height > width;
  const hudHeight = portrait ? 78 : 68;
  const navigationHeight = portrait ? 82 : 72;
  const cellSize = portrait ? 62 : 72;
  const mapViewport: Rect = {
    x: portrait ? 0 : 24,
    y: hudHeight,
    width: portrait ? width : width - 48,
    height: height - hudHeight - navigationHeight
  };
  const columns = Math.max(1, Math.ceil(mapViewport.width / cellSize));
  const rows = Math.max(1, Math.ceil(mapViewport.height / cellSize));
  const originX = clamp(
    player.x - Math.floor(columns / 2),
    0,
    Math.max(0, mapWidth - columns)
  );
  const originY = clamp(
    player.y - Math.floor(rows / 2),
    0,
    Math.max(0, mapHeight - rows)
  );
  return {
    width,
    height,
    mode: portrait ? "portrait" : "landscape",
    hudHeight,
    navigationHeight,
    mapViewport: {
      ...mapViewport,
      cellSize,
      columns,
      rows,
      originX,
      originY
    }
  };
};

export interface RpgCharacterWindowLayout {
  panel: Rect;
  closeButton: Rect;
  stats: Rect;
  equipment: Rect;
  inventory: Rect;
}

/** Shared desktop/portrait layout for the classic character + backpack window. */
export const computeRpgCharacterWindowLayout = (
  width: number,
  height: number
): RpgCharacterWindowLayout => {
  const portrait = height > width;
  const panel: Rect = portrait
    ? { x: 10, y: 18, width: width - 20, height: height - 36 }
    : { x: 30, y: 24, width: width - 60, height: height - 48 };
  const top = panel.y + 62;
  if (portrait) {
    return {
      panel,
      closeButton: {
        x: panel.x + panel.width - 50,
        y: panel.y + 12,
        width: 38,
        height: 38
      },
      stats: {
        x: panel.x + 14,
        y: top,
        width: panel.width - 28,
        height: 104
      },
      equipment: {
        x: panel.x + 14,
        y: top + 116,
        width: panel.width - 28,
        height: 262
      },
      inventory: {
        x: panel.x + 14,
        y: top + 390,
        width: panel.width - 28,
        height: panel.y + panel.height - (top + 402)
      }
    };
  }
  const left: Rect = {
    x: panel.x + 18,
    y: top,
    width: panel.width * 0.47 - 24,
    height: panel.height - 80
  };
  return {
    panel,
    closeButton: {
      x: panel.x + panel.width - 50,
      y: panel.y + 12,
      width: 38,
      height: 38
    },
    stats: { x: left.x, y: left.y, width: left.width, height: 104 },
    equipment: {
      x: left.x,
      y: left.y + 116,
      width: left.width,
      height: left.height - 116
    },
    inventory: {
      x: panel.x + panel.width * 0.47 + 6,
      y: top,
      width: panel.width * 0.53 - 24,
      height: panel.height - 80
    }
  };
};

export interface RpgBattleLayout {
  width: number;
  height: number;
  mode: "portrait" | "landscape";
  header: Rect;
  playerArea: Rect;
  enemyArea: Rect;
  playerStatus: Rect;
  actionButtons: Rect[];
  settingButtons: Rect[];
  logArea: Rect;
}

/** Shared battle scene layout for desktop and portrait RPGs. */
export const computeRpgBattleLayout = (
  width: number,
  height: number,
  actionCount = 3
): RpgBattleLayout => {
  const portrait = height > width;
  const header: Rect = { x: 0, y: 0, width, height: 58 };
  const buttonY = height - 70;
  const sceneBottom = height - 138;
  const playerArea: Rect = portrait
    ? { x: 14, y: 500, width: width - 28, height: Math.max(1, sceneBottom - 500) }
    : { x: 24, y: 66, width: width * 0.4, height: Math.max(1, sceneBottom - 66) };
  const enemyArea: Rect = portrait
    ? { x: 14, y: 64, width: width - 28, height: 430 }
    : { x: width * 0.48, y: 66, width: width * 0.5 - 24, height: Math.max(1, sceneBottom - 66) };
  const playerStatus: Rect = portrait
    ? { x: 24, y: Math.min(510, sceneBottom - 58), width: width - 48, height: 54 }
    : { x: 40, y: sceneBottom - 60, width: Math.min(400, width * 0.36), height: 54 };
  const gap = 10;
  const settingGap = 6;
  const settingWidth = portrait ? 76 : 92;
  const settingTotalWidth = settingWidth * 2 + settingGap;
  const totalWidth = portrait
    ? width - 28
    : Math.min(860, width - 62 - settingTotalWidth);
  const buttonWidth =
    (totalWidth - gap * Math.max(0, actionCount - 1)) /
    Math.max(1, actionCount);
  const startX = portrait ? 14 : 24;
  const actionButtons = Array.from({ length: actionCount }, (_, index) => ({
    x: startX + index * (buttonWidth + gap),
    y: buttonY,
    width: buttonWidth,
    height: 54
  }));
  const settingY = portrait ? buttonY - 42 : buttonY;
  const settingButtons = Array.from({ length: 2 }, (_, index) => ({
    x: width - 24 - settingTotalWidth + index * (settingWidth + settingGap),
    y: settingY,
    width: settingWidth,
    height: portrait ? 32 : 54
  }));
  return {
    width,
    height,
    mode: portrait ? "portrait" : "landscape",
    header,
    playerArea,
    enemyArea,
    playerStatus,
    actionButtons,
    settingButtons,
    logArea: {
      x: startX,
      y: sceneBottom + 8,
      width: portrait ? totalWidth - settingTotalWidth - 12 : totalWidth,
      height: Math.max(1, buttonY - sceneBottom - 12)
    }
  };
};

export const computeRpgBattleEnemySlots = (
  layout: RpgBattleLayout,
  enemyCount: number
): Rect[] => {
  const count = Math.max(1, enemyCount);
  const columnWidth = (layout.enemyArea.width - 40) / count;
  const slotWidth = Math.min(
    layout.mode === "portrait" ? 140 : 220,
    columnWidth
  );
  return Array.from({ length: count }, (_, index) => ({
    x:
      layout.enemyArea.x +
      20 +
      index * columnWidth +
      (columnWidth - slotWidth) / 2,
    y: layout.enemyArea.y + (layout.mode === "portrait" ? 42 : 18),
    width: slotWidth,
    height: layout.mode === "portrait"
      ? 210
      : Math.min(300, layout.enemyArea.height - 34)
  }));
};

export type RpgBattleSpeed = 1 | 2;

/**
 * Data-neutral auto-battle scheduler. Rules stay in the game controller; the
 * framework only decides when the next action callback is due.
 */
export class RpgBattlePacer {
  private elapsedMs = 0;
  private speedValue: RpgBattleSpeed = 1;

  speed(): RpgBattleSpeed {
    return this.speedValue;
  }

  setSpeed(speed: RpgBattleSpeed): void {
    this.speedValue = speed;
  }

  toggleSpeed(): RpgBattleSpeed {
    this.speedValue = this.speedValue === 1 ? 2 : 1;
    return this.speedValue;
  }

  reset(): void {
    this.elapsedMs = 0;
  }

  update(
    deltaMs: number,
    options: {
      active: boolean;
      auto: boolean;
      intervalMs: number;
      performAction: () => boolean;
    }
  ): boolean {
    if (!options.active || !options.auto) {
      this.reset();
      return false;
    }
    this.elapsedMs +=
      Math.max(0, deltaMs) * this.speedValue;
    if (this.elapsedMs < Math.max(1, options.intervalMs)) return false;
    this.elapsedMs %= Math.max(1, options.intervalMs);
    return options.performAction();
  }
}

export interface RpgInventoryGridLayout {
  columns: number;
  rows: number;
  pageSize: number;
  cells: Rect[];
  pager: Rect;
}

export const computeRpgInventoryGridLayout = (
  rect: Rect,
  options: {
    columns?: number;
    rows?: number;
    portrait?: boolean;
    headerHeight?: number;
    footerHeight?: number;
  } = {}
): RpgInventoryGridLayout => {
  const columns = Math.max(1, options.columns ?? 8);
  const rows = Math.max(1, options.rows ?? 8);
  const gap = options.portrait ? 2 : 4;
  const headerHeight = options.headerHeight ?? 34;
  const footerHeight = options.footerHeight ?? 32;
  const cellWidth = (rect.width - gap * (columns - 1)) / columns;
  const cellHeight =
    (rect.height -
      headerHeight -
      footerHeight -
      gap * (rows - 1)) /
    rows;
  const cells = Array.from(
    { length: columns * rows },
    (_, index): Rect => {
      const column = index % columns;
      const row = Math.floor(index / columns);
      return {
        x: rect.x + column * (cellWidth + gap),
        y: rect.y + headerHeight + row * (cellHeight + gap),
        width: cellWidth,
        height: cellHeight
      };
    }
  );
  return {
    columns,
    rows,
    pageSize: columns * rows,
    cells,
    pager: {
      x: rect.x,
      y: rect.y + rect.height - footerHeight,
      width: rect.width,
      height: footerHeight
    }
  };
};

export interface RpgBulkSaleEntry {
  id: string;
  qualityId: number;
  salePrice: number;
  locked: boolean;
  equipped: boolean;
  exceptional: boolean;
}

export interface RpgBulkSaleQuote {
  ids: string[];
  count: number;
  gold: number;
}

export const quoteRpgBulkSale = (
  entries: readonly RpgBulkSaleEntry[],
  options: {
    qualityIds: ReadonlySet<number>;
    includeExceptional?: boolean;
  }
): RpgBulkSaleQuote => {
  const selected = entries.filter(
    (entry) =>
      options.qualityIds.has(entry.qualityId) &&
      !entry.locked &&
      !entry.equipped &&
      entry.salePrice > 0 &&
      (options.includeExceptional === true || !entry.exceptional)
  );
  return {
    ids: selected.map((entry) => entry.id),
    count: selected.length,
    gold: selected.reduce((sum, entry) => sum + entry.salePrice, 0)
  };
};

export const computeP0Layout = (
  width: number,
  height: number,
  mapWidth: number,
  mapHeight: number,
  player: GridPoint
): P0Layout => {
  const portrait = height > width;
  const cellSize = portrait ? 52 : 48;
  const mapRect: Rect = portrait
    ? {
        x: 16,
        y: 108,
        width: Math.max(1, width - 32),
        height: Math.max(1, height - 228)
      }
    : {
        x: 24,
        y: 82,
        width: Math.min(624, Math.max(1, width - 300)),
        height: Math.max(1, height - 106)
      };
  const columns = Math.max(1, Math.floor(mapRect.width / cellSize));
  const rows = Math.max(1, Math.floor(mapRect.height / cellSize));
  const originX = clamp(
    player.x - Math.floor(columns / 2),
    0,
    Math.max(0, mapWidth - columns)
  );
  const originY = clamp(
    player.y - Math.floor(rows / 2),
    0,
    Math.max(0, mapHeight - rows)
  );
  const primaryButton = portrait
    ? { x: 24, y: height - 92, width: width - 48, height: 60 }
    : { x: width - 276, y: height - 92, width: 236, height: 58 };
  const secondaryButton = portrait
    ? { x: 24, y: height - 164, width: width - 48, height: 52 }
    : { x: width - 276, y: height - 160, width: 236, height: 52 };
  const menuButton = portrait
    ? { x: width - 76, y: 20, width: 56, height: 56 }
    : { x: width - 80, y: 18, width: 56, height: 48 };
  const modal = portrait
    ? { x: 20, y: height * 0.25, width: width - 40, height: 260 }
    : { x: width / 2 - 220, y: height / 2 - 140, width: 440, height: 280 };
  const modalCloseButton = {
    x: modal.x + 24,
    y: modal.y + modal.height - 76,
    width: modal.width - 48,
    height: 52
  };

  return {
    mode: portrait ? "portrait" : "landscape",
    width,
    height,
    hud: { x: 0, y: 0, width, height: portrait ? 92 : 70 },
    mapViewport: {
      ...mapRect,
      cellSize,
      originX,
      originY,
      columns,
      rows
    },
    primaryButton,
    secondaryButton,
    menuButton,
    modal,
    modalCloseButton,
    playerArea: portrait
      ? { x: 24, y: height * 0.54, width: width - 48, height: 170 }
      : { x: 48, y: 130, width: width * 0.35, height: height - 250 },
    enemyArea: portrait
      ? { x: 24, y: 110, width: width - 48, height: 220 }
      : {
          x: width * 0.58,
          y: 110,
          width: width * 0.34,
          height: height - 220
        }
  };
};

export class WindowStack {
  private windows: Array<"menu"> = [];

  top(): "menu" | null {
    return this.windows.at(-1) ?? null;
  }

  open(window: "menu"): void {
    if (this.top() !== window) {
      this.windows.push(window);
    }
  }

  closeTop(): void {
    this.windows.pop();
  }

  clear(): void {
    this.windows.length = 0;
  }
}

export class P0GameController implements GameControllerPort {
  private state: GameState;
  private revision = 0;
  private events: ControllerSnapshot["events"] = [];
  private readonly listeners = new Set<(snapshot: ControllerSnapshot) => void>();
  private readonly windows = new WindowStack();

  constructor(
    initialState: GameState,
    private readonly content: ContentBundle
  ) {
    this.state = initialState;
  }

  getSnapshot(): ControllerSnapshot {
    return {
      revision: this.revision,
      state: this.state,
      events: this.events,
      topWindow: this.windows.top()
    };
  }

  subscribe(listener: (snapshot: ControllerSnapshot) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  dispatch(action: GameAction): ControllerSnapshot {
    if (action.type === "open-menu") {
      if (this.windows.top()) {
        this.windows.closeTop();
      } else if (this.state.scene !== "title" && this.state.scene !== "result") {
        this.windows.open("menu");
      }
      return this.publish([]);
    }

    if (this.windows.top()) {
      if (
        action.type === "cancel" ||
        action.type === "confirm" ||
        action.type === "close-window"
      ) {
        this.windows.closeTop();
        return this.publish([]);
      }
      return this.getSnapshot();
    }

    if (
      action.type === "cancel" &&
      (this.state.scene === "map" || this.state.scene === "battle")
    ) {
      this.windows.open("menu");
      return this.publish([]);
    }

    const transition = reduceGame(this.state, action, this.content);
    if (transition.state === this.state && transition.events.length === 0) {
      return this.getSnapshot();
    }
    this.state = transition.state;
    return this.publish(transition.events);
  }

  resolvePoint(
    x: number,
    y: number,
    width: number,
    height: number
  ): GameAction | null {
    const state = this.state;
    const map = this.content.maps[state.mapId];
    if (!map) {
      return null;
    }
    const layout = computeP0Layout(
      width,
      height,
      map.width,
      map.height,
      state.position
    );
    if (this.windows.top()) {
      return contains(layout.modalCloseButton, x, y)
        ? { type: "close-window" }
        : null;
    }
    if (contains(layout.menuButton, x, y)) {
      return { type: "open-menu" };
    }
    if (state.scene === "title" && contains(layout.primaryButton, x, y)) {
      return { type: "confirm" };
    }
    if (state.scene === "map" && contains(layout.mapViewport, x, y)) {
      const targetX =
        layout.mapViewport.originX +
        Math.floor((x - layout.mapViewport.x) / layout.mapViewport.cellSize);
      const targetY =
        layout.mapViewport.originY +
        Math.floor((y - layout.mapViewport.y) / layout.mapViewport.cellSize);
      const dx = targetX - state.position.x;
      const dy = targetY - state.position.y;
      if (Math.abs(dx) + Math.abs(dy) === 1) {
        return {
          type: "move",
          dx: dx as -1 | 0 | 1,
          dy: dy as -1 | 0 | 1
        };
      }
      return null;
    }
    if (state.scene === "battle") {
      if (
        state.battle?.won &&
        contains(layout.primaryButton, x, y)
      ) {
        return { type: "confirm" };
      }
      if (
        !state.battle?.won &&
        contains(layout.primaryButton, x, y)
      ) {
        return { type: "basic-attack" };
      }
    }
    if (state.scene === "result" && contains(layout.primaryButton, x, y)) {
      return { type: "confirm" };
    }
    return null;
  }

  private publish(events: ControllerSnapshot["events"]): ControllerSnapshot {
    this.events = events;
    this.revision += 1;
    const snapshot = this.getSnapshot();
    for (const listener of this.listeners) {
      listener(snapshot);
    }
    return snapshot;
  }
}
